import { DEFAULT_FORM_DEF } from "formdefs/formDefMap.js";
import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { useSnackbar } from "notistack";
import { useAtom } from "jotai";
import { backendDataAtom } from "atoms/backendDataAtom.js";
import { useNavigate } from "react-router";
import { flushSync } from "react-dom";
import {
  backendGet,
  backendPost,
  backendPatch,
} from "@accredo/fe-shared/services/dataService.js";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard.js";
import UnsavedChangesModal from "../UnsavedChangesModal.jsx";
import NewFormRenderer from "../Form/FormDefinitionRenderer/NewFormRenderer.jsx";
import CoverageSplitDialog from "../modals/CoverageSplitDialog.jsx";
import { COVERAGE_TYPES_FIELD } from "../Form/FormDefinitionRenderer/coverageSelection.js";

function SchadenFormComponent({
  model,
  hideTitle,
  disable,
  formDefinition,
  objektSparten,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // The saved claim whose Sparten the user is asked about before reporting.
  const [splitCandidate, setSplitCandidate] = useState(null);
  const [suppressUnsavedChangesGuard, setSuppressUnsavedChangesGuard] =
    useState(false);
  const is_mounted_ref = useRef(true);
  const { enqueueSnackbar } = useSnackbar();
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const navigate = useNavigate();

  const {
    showModal: showUnsavedChangesModal,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesGuard(isDirty && !suppressUnsavedChangesGuard);

  useEffect(() => {
    return () => {
      is_mounted_ref.current = false;
    };
  }, []);

  useEffect(() => {
    setSuppressUnsavedChangesGuard(false);
  }, [model?.id]);

  const handleDirtyChange = useCallback((dirty) => {
    setIsDirty(dirty);
    // console.log("dirty?", dirty);
  }, []);

  /**
   * Navigate to a new route without showing the unsaved changes guard.
   * Since it deliberately bypasses the unsaved changes guard, it must
   * only be used for navigating after a successful save.
   */
  const navigateWithoutUnsavedChangesGuard = useCallback(
    (to) => {
      flushSync(() => {
        setIsDirty(false);
        setSuppressUnsavedChangesGuard(true);
      });

      navigate(to);

      queueMicrotask(() => {
        if (is_mounted_ref.current) {
          setSuppressUnsavedChangesGuard(false);
        }
      });
    },
    [navigate]
  );

  const schadenGespeichert = (data) => {
    enqueueSnackbar(
      "Schaden wurde gespeichert - ACHTUNG: damit ist er noch nicht zur Prüfung gemeldet!",
      { variant: "success" }
    );
    setBackendData({ data: data, id: data.id, type: "schaden" });
    navigateWithoutUnsavedChangesGuard(
      "/schaeden/" + data.id + "?tab=schadenformular"
    );
  };

  const schadenGemeldet = (data) => {
    enqueueSnackbar("Schaden wurde zur Prüfung gemeldet", {
      variant: "success",
    });
    setBackendData({ data: data, id: data.id, type: "schaden" });
    navigateWithoutUnsavedChangesGuard(
      "/schaeden/" + data.id + "?tab=schadenformular&dialog=pre_check"
    );
  };

  const submitCallback = async (formData) => {
    const id = model?.id;
    if (!id) {
      return await backendPost("data/schadens", {
        toSend: formData,
        setIsLoading,
      });
    } else {
      return await backendPatch(`data/schadens/${id}/formdata`, {
        toSend: formData,
        setIsLoading,
      });
    }
  };

  const fdef = formDefinition ?? DEFAULT_FORM_DEF;

  const isCoverageForm = !!fdef.coverage_types?.length;

  const formContent = (
    <NewFormRenderer
      def={fdef}
      objektSparten={objektSparten}
      model={model}
      editMode={!disable}
      progressMode={model?.id ? "full" : "lax"}
      onDirtyChange={handleDirtyChange}
      actions={[
        {
          type: "submit",
          label: "Speichern",
          enabled: "always",
          validate: false,
          method: "callback",
          callback: async (formdata) => {
            const data = await submitCallback(formdata);
            if (!data) return;
            schadenGespeichert(data);
          },
        },
        {
          type: "submit",
          label: "zur Prüfung melden",
          // The form-wide schema also requires the fields of unselected
          // Sparte steps, so a coverage form is gated and submitted on its
          // per-Sparte validity instead.
          enabled: ({ isValid, coverage }) =>
            (isCoverageForm
              ? coverage.shared &&
                Object.values(coverage.coverage).some(Boolean)
              : isValid) &&
            !["pre_check", "customer_pre_check"].includes(model?.status?.value),
          validate: !isCoverageForm,
          method: "callback",
          callback: async (formdata) => {
            const data = await submitCallback(formdata);
            if (!data) return;
            if (isCoverageForm && formdata[COVERAGE_TYPES_FIELD]?.length > 1) {
              const saved = await backendGet(`data/schaden/${data.id}`);
              if (saved?.permissions?.can_split) {
                setSplitCandidate(saved);
                return;
              }
            }
            await backendPatch(`data/schadens/${data.id}/status`, {
              toSend: { status: "pre_check" },
              setIsLoading,
              setResult: (updatedData) => {
                schadenGemeldet(updatedData);
              },
            });
          },
        },
      ]}
    />
  );

  const cancelSplit = () => {
    const saved = splitCandidate;
    setSplitCandidate(null);
    schadenGespeichert(saved);
  };

  const claimSplit = async () => {
    const claim = await backendGet(`data/schaden/${splitCandidate.id}`);
    setSplitCandidate(null);
    if (claim) schadenGemeldet(claim);
  };

  const splitDialog = (
    <CoverageSplitDialog
      open={!!splitCandidate}
      claim={splitCandidate}
      formDefinition={fdef}
      onClose={cancelSplit}
      onSplit={claimSplit}
    />
  );

  if (isLoading) return <Spinner />;

  if (hideTitle) {
    return (
      <>
        {formContent}
        {splitDialog}
        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </>
    );
  } else {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h3">Schadenformular</Typography>
        {formContent}
        {splitDialog}
        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </Box>
    );
  }
}

export default SchadenFormComponent;
